// Page warming in small, resumable batches (card #4117).
//
// A sync used to hand the service worker its whole list of pages (284 or more
// for a heavy user) in one message. On a weak signal the pass gave up part way
// (158 saved, 126 abandoned on one phone) and nothing tried the rest until the
// next full sync. Now:
//
//   - the list arrives already ordered, most important first (see
//     warmUrlsForSync in field_offline_resources.js);
//   - it is sent in batches of WARM_BATCH_SIZE and each batch is read back, so
//     the sync knows what really got cached;
//   - anything that did not get cached is kept in field:warm-pending and tried
//     again, a batch at a time, when the signal is healthy (resumeWarm, called
//     by the always-mounted auto-sync controller).
//
// The service worker acks every URL with its outcome (cached | failed |
// aborted | skipped); an ack that never arrives counts as failed.
//
// Card #4659 -- healShellCache() below is a different retry: not resource
// pages warmed off a sync, but the SHELL/ASSET caches an install may have
// left incomplete on weak signal. Same self-limiting shape, same caller
// (_catchUp).

import { currentLevel, LEVEL_ONLINE } from "./field_network_health"
import { logEvent, logWarn } from "./field_logger"

export const WARM_BATCH_SIZE = 12
const BATCH_TIMEOUT_MS = 20000
const PENDING_KEY = "field:warm-pending"
const PENDING_MAX = 600
const RESUME_MIN_GAP_MS = 60000
// Two batches in a row with nothing cached means the signal is not carrying
// pages right now. Stop and keep the rest for later.
const STALLED_BATCHES = 2

function readPending() {
  try {
    const parsed = JSON.parse(localStorage.getItem(PENDING_KEY) || "[]")
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function writePending(urls) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify(urls.slice(0, PENDING_MAX))) } catch { /* private window */ }
}

export function pendingWarmUrls() {
  return readPending()
}

// Sends one batch to the service worker and waits for every ack (or the
// timeout). Resolves with { cached: [], failed: [] }.
function warmBatch(sw, urls, timeoutMs) {
  return new Promise(resolve => {
    const cached = []
    const seen = new Set()
    const channel = new MessageChannel()
    let timer = null

    const finish = () => {
      clearTimeout(timer)
      channel.port1.onmessage = null
      const failed = urls.filter(url => !cached.includes(url))
      resolve({ cached, failed })
    }

    channel.port1.onmessage = (event) => {
      const { url, outcome } = event.data || {}
      if (!url || seen.has(url)) return
      seen.add(url)
      // An ack from an older service worker carries no outcome: treat it as cached.
      if (outcome === undefined || outcome === "cached") cached.push(url)
      if (seen.size >= urls.length) finish()
    }

    timer = setTimeout(finish, timeoutMs)
    sw.postMessage({ type: "WARM_CACHE", urls }, [channel.port2])
  })
}

// Warms `urls` in order, batch by batch. onProgress({ done, total, cached })
// fires after each batch. Anything not cached is saved for resumeWarm.
// Never throws.
export async function warmInBatches(urls, { onProgress = () => {}, batchSize = WARM_BATCH_SIZE, batchTimeoutMs = BATCH_TIMEOUT_MS, trigger = "sync" } = {}) {
  const sw = navigator.serviceWorker?.controller
  const total = urls.length
  const startedAt = Date.now()
  let done = 0, cachedCount = 0, stalled = 0
  const notCached = []

  if (!sw || total === 0) {
    return { attempted: 0, cached: 0, pending: readPending().length, total }
  }

  let index = 0
  for (; index < total; index += batchSize) {
    if (currentLevel() !== LEVEL_ONLINE) break
    const batch = urls.slice(index, index + batchSize)
    const result = await warmBatch(sw, batch, batchTimeoutMs)
    cachedCount += result.cached.length
    notCached.push(...result.failed)
    done += batch.length
    stalled = result.cached.length === 0 ? stalled + 1 : 0
    onProgress({ done, total, cached: cachedCount })
    if (stalled >= STALLED_BATCHES) { index += batchSize; break }
  }

  // Everything after the point we stopped at was never attempted.
  const neverAttempted = urls.slice(Math.min(index, total))
  const pending = [...new Set([...notCached, ...neverAttempted])]
  writePending(pending)

  const logger = pending.length > 0 ? logWarn : logEvent
  logger("sync", "Page warm batches", {
    trigger, total, attempted: done, cached: cachedCount, pending: pending.length,
    durationMs: Date.now() - startedAt
  }).catch(() => {})

  return { attempted: done, cached: cachedCount, pending: pending.length, total }
}

let resuming = false
let lastResumeAt = 0

// Picks up pages a pass could not cache. Only on a healthy connection, at most
// once a minute, one run at a time.
export async function resumeWarm({ trigger = "auto", force = false } = {}) {
  if (resuming) return { ran: false }
  const pending = readPending()
  if (pending.length === 0) return { ran: false }
  if (!navigator.serviceWorker?.controller) return { ran: false }
  if (currentLevel() !== LEVEL_ONLINE || navigator.onLine === false) return { ran: false }
  if (!force && Date.now() - lastResumeAt < RESUME_MIN_GAP_MS) return { ran: false }

  resuming = true
  lastResumeAt = Date.now()
  try {
    const result = await warmInBatches(pending, { trigger })
    return { ran: true, ...result }
  } finally {
    resuming = false
  }
}

// Card #4659 -- asks the service worker to retry any manifest-declared shell
// page or build asset still missing from SHELL_CACHE/ASSET_CACHE (the
// HEAL_SHELL_CACHE message, field_service_worker.js's _healShellCache()).
// Previously that pass only ran automatically on 'activate' -- a weak-signal
// install that left a gap stayed broken until the NEXT app update. Called
// from field_auto_sync_controller.js's _catchUp alongside resumeWarm, so the
// same three triggers apply: app open, reconnect, and returning to the
// foreground. Same shape as resumeWarm above: a healthy connection only, one
// run at a time, at most once a minute (the pass itself is cheap -- this just
// stops it firing on every single trigger back to back).
//
// Targets BOTH registration.active and registration.waiting, not just
// navigator.serviceWorker.controller. The install gate (field_service_worker.js's
// 'install' listener) defers skipWaiting() until a new worker's shell/asset
// cache is genuinely complete -- while that's unresolved, .controller is
// still the OLD worker (already complete, nothing to heal), and the one that
// actually needs this pass sits in registration.waiting instead, untouched by
// any message sent only to .controller. Without reaching it directly, a
// weak-signal install stays stuck waiting until every open tab happens to
// close and reopen -- which a field Tasker running one tab across a whole
// shift may never do. field_service_worker.js's handler promotes that worker
// itself (self.skipWaiting()) once this pass confirms it's complete.
//
// Card #4861 -- this function already refuses to send HEAL_SHELL_CACHE at all
// unless the page's own currentLevel() is LEVEL_ONLINE, so a 2g/3g page never
// triggers the message in the first place. The other half of the throttle --
// bounding how many pages/assets the worker fetches in parallel once a
// message does land, and skipping the pass if the worker's OWN connectivity
// evidence has gone Degraded since -- lives in
// field_service_worker.js's _healShellCache() (reuses the WARM_CONCURRENCY
// bounded pool card #3766 already proved for the sibling WARM_CACHE pass).
const HEAL_MIN_GAP_MS = 60000
const HEAL_TIMEOUT_MS = 15000
let healing = false
let lastHealAt = 0

export async function healShellCache({ trigger = "auto", force = false } = {}) {
  if (healing) return { ran: false }
  if (!navigator.serviceWorker) return { ran: false }
  if (currentLevel() !== LEVEL_ONLINE || navigator.onLine === false) return { ran: false }
  if (!force && Date.now() - lastHealAt < HEAL_MIN_GAP_MS) return { ran: false }

  const registration = await navigator.serviceWorker.getRegistration().catch(() => null)
  const targets = [...new Set([registration?.active, registration?.waiting].filter(Boolean))]
  if (targets.length === 0) return { ran: false }

  healing = true
  lastHealAt = Date.now()
  try {
    await Promise.all(targets.map(_healOne))
    logEvent("sync", "Shell cache heal requested", { trigger, targets: targets.length }).catch(() => {})
    return { ran: true }
  } finally {
    healing = false
  }
}

// Sends HEAL_SHELL_CACHE to one worker (active or waiting -- see
// healShellCache() above) and waits for its ack, or the timeout.
function _healOne(sw) {
  return new Promise(resolve => {
    const channel = new MessageChannel()
    const timer = setTimeout(resolve, HEAL_TIMEOUT_MS)
    channel.port1.onmessage = (event) => {
      if (event.data?.type === "SHELL_CACHE_HEALED") {
        clearTimeout(timer)
        resolve()
      }
    }
    sw.postMessage({ type: "HEAL_SHELL_CACHE" }, [channel.port2])
  })
}
